import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabGroup } from '@angular/material/tabs';
import { isEmpty, merge } from 'lodash';
import { Subscription } from 'rxjs';
import { IBreadcrumbData } from '../../../interfaces/breadcrumb-data.interface';
import { AppConfigService } from '../../../services/app-config.service';
import { ConfigService } from '../../../services/config.service';
import { DeleteDialogComponent } from '../dialogs/delete-dialog/delete-dialog.component';
import { ImportSheetComponent } from '../dialogs/import-sheet/import-sheet.component';
import { SaveDialogComponent, SaveDialogResult } from '../dialogs/save-dialog/save-dialog.component';

const IPValidator: ValidatorFn = Validators.pattern(
  '^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]).){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$'
);
const numberValidator: ValidatorFn = Validators.pattern('^[0-9]*$');

const WEB_CLIENT_TAB_IDX: number = 6;
interface General {
  name: string;
  info: {
    code: string;
    country: string;
    latitude: number;
    longitude: number;
    enclosures: Enclosure[];
  };
}
interface Servmat {
  server: {
    mapName: string;
    IP: string;
    port: number;
    logDir: string;
    logLevel: number;
  };
}
interface ComServer {
  com_server: {
    mapName: string;
    IP: string;
    port: number;
    logDir: string;
    logLevel: number;
    devices: {
      ioDevices: Device[];
      readers: Device[];
      printers: Device[];
      fuel_dispenser_lines: Device[];
      weighers: Device[];
    };
  };
}
interface AuthServer {
  auth_server: {
    mapName: string;
    IP: string;
    port: number;
    logDir: string;
    openAlways: boolean;
    loadServices: boolean;
    loadPRLProviderEmployees: boolean;
    mysql: {
      host: string;
      database: string;
    };
  };
}
interface WebClient {
  serverURL: string;
  wsURI: string;
  liveViewUpdateTime: number;
}

interface OCRServersContainer {
  ocr_servers: OCRServer[];
}

interface OCRServer {
  id: number;
  mapName: string;
  IP: string;
  port: number;
  eventsDir: string;
  imagesDir: string;
  logDir: string;
  logLevel: number;
  engines: Engine[];
  cameras: Camera[];
}

interface LanesContainer {
  lanes: Lane[];
}
interface Lane {
  id: number;
  mapName: string;
  general: {
    laneType: string;
    direction: string;
    encFrom: number;
    encTo: number;
    statesMachine: string;
    bidireccional: boolean;
    reverseLane: number;
    autoDetecDirectionMode: boolean;
    laneEnabled: boolean;
  };
  identification: {
    presence: {
      deviceId: number;
      io: string;
      inv: boolean;
    };
    plates: {
      front: [
        {
          ocrServerId: number;
          camId: number;
          trigger: string;
          getPlateFromAuthServer: boolean;
          previousLane: number;
        }
      ];
      rears: [
        {
          ocrServerId: number;
          camId: number;
          trigger: string;
          getPlateFromAuthServer: boolean;
          previousLane: number;
        }
      ];
    };
  };
  gate: {
    sensors: {
      security: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
      bar_open: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
      closed: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
      blocked: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
      photocell: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
    };
    actions: {
      open: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
      close: {
        deviceId: number;
        io: string;
        inv: boolean;
      };
    };
  };
}
interface Enclosure {
  id: number;
  name: string;
}
interface Device {
  id: number;
  mapName: string;
  IP: string;
  port: number;
  driver: string;
}
interface Engine {
  id: number;
  mapName: string;
  type: string;
  name: string;
  coresLicensed: number;
  searchAllPlatesInFrame: boolean;
  changeParamsPerCamera: boolean;
}
interface Camera {
  id: number;
  mapName: string;
  url: string;
  engineId: number;
  maxPictures: number;
  minConfidence: number;
  stopReadingIfConf: number;
  msBetweenImagesEvent: number;
  method: string;
  size: number;
  slant: number;
  slope: number;
}

@Component({
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.css'],
})
export class ConfigurationComponent implements OnInit, OnDestroy {
  @ViewChild('formTabs') formTabs: MatTabGroup;
  @ViewChild('ocrServers') ocrServersTabGroup: MatTabGroup;
  @ViewChild('lanes') lanesTabGroup: MatTabGroup;

  generalConfigJSON: any = {};
  servmatConfigJSON: any = {};
  comServerConfigJSON: any = {};
  ocrServersConfigJSON: any = {};
  authServerConfigJSON: any = {};
  lanesConfigJSON: any = {};
  webClientConfigJSON: any = {};
  formsJSON: any = {};

  isBackup: boolean = false;

  infoIds: string[] = ['code', 'country', 'latitude', 'longitude'];
  infoNames: string[] = ['Código', 'País', 'Latitud', 'Longitud'];

  serverIds: string[] = ['mapName', 'IP', 'port', 'logDir'];
  serverNames: string[] = ['Nombre', 'IP', 'Puerto', 'Directorio logs'];
  logsLevel: string[] = ['Warning', 'Error', 'Debug'];

  comServerIds: string[] = ['mapName', 'IP', 'port', 'logDir'];
  comServerNames: string[] = ['Nombre', 'IP', 'Puerto', 'Directorio logs'];

  ocrServerIds: string[] = ['eventsDir', 'imagesDir', 'logDir'];
  ocrServerNames: string[] = ['Directorio eventos', 'Directorio imagenes', 'Directorio logs'];

  devicesIds: string[] = ['id', 'mapName', 'IP', 'port'];
  devicesNames: string[] = ['Id', 'Nombre', 'IP', 'Puerto'];
  ioDeviceDrivers: string[] = ['ElkaTCP_v1.0', 'ElkaTCP_v1.1', 'ioLogik'];
  readerDrivers: string[] = ['Newland_FM430', 'Mifare_Promag_ER750'];
  printerDrivers: string[] = ['Custom_kpm_150h', 'Custom_Vkp_80III'];
  fuelDispenserDrivers: string[] = ['Gilbarco_Dispensers'];
  weighersDrivers: string[] = ['NPortComWeigherDriver'];

  engineIds: string[] = ['id', 'mapName', 'name', 'coresLicensed'];
  engineNames: string[] = ['Id', 'Nombre', 'Motor', 'Cores'];
  engineTypes: string[] = ['ANPR', 'ACCR'];

  cameraIds: string[] = ['id', 'mapName', 'maxPictures', 'minConfidence', 'stopReadingIfConf', 'msBetweenImagesEvent'];
  cameraNames: string[] = ['ID', 'Nombre', 'Max Pic', 'Conf', 'SRIT', 'TBIE'];
  cameraIds2: string[] = ['url', 'size', 'slant', 'slope'];
  cameraNames2: string[] = ['URL', 'Tamaño', 'Inclinación', 'Pendiente'];

  methods: string[] = ['onePlate', 'allPlates'];

  authServerIds: string[] = ['mapName', 'IP', 'port', 'logDir'];
  authServerNames: string[] = ['Nombre', 'IP', 'Puerto', 'Directorio log'];

  laneTypes: string[] = ['vehicles', 'peatons', 'kiosk'];
  laneDirections: string[] = ['in', 'out'];

  io1: string[] = ['-1', 'bar_open', 'closed', 'blocked', 'loopA', 'loopB', 'loopC', 'photocell'];
  io2: string[] = ['-1', 'di0', 'di1', 'di2', 'di3', 'di4', 'di5'];

  sensorIds: string[] = ['bar_open', 'closed', 'blocked', 'security', 'photocell'];
  sensorNames: string[] = ['Barrera Abierta', 'Barrera Cerrada', 'Barrera Bloqueada', 'Seguridad', 'Fotocélula'];

  actionIds: string[] = ['open', 'close'];
  actionNames: string[] = ['Abrir', 'Cerrar'];

  webClientIds: string[] = ['serverURL', 'wsURI', 'liveViewUpdateTime'];
  webClientNames: string[] = ['Server URL', 'Web Socket URI', 'Refresh Live View'];

  numericFields: string[] = [
    'latitude',
    'longitude',
    'id',
    'port',
    'Port',
    'logLevel',
    'coresLicensed',
    'engineId',
    'maxPictures',
    'minConfidence',
    'stopReadingIfConf',
    'msBetweenImagesEvent',
    'size',
    'slant',
    'slope',
    'encFrom',
    'encTo',
    'deviceId',
    'previousLane',
    'retriesOCRMax',
    'camId',
    'ocrServerId',
    'dispenserLineId',
    'dispenserId',
  ];

  private configSubscription: Subscription;
  private configBackupSubscription: Subscription;

  public cfg: any;
  public wccfg: any;

  public generalForm: FormGroup;
  public servmatForm: FormGroup;
  public comserverForm: FormGroup;
  public ocrserversForm: FormGroup;
  public authserverForm: FormGroup;
  public lanesForm: FormGroup;
  public webclientForm: FormGroup;

  public breadcrumbData: IBreadcrumbData;

  private generalConfig: General = {
    name: 'NUEVO CAMPING',
    info: {
      code: '1235',
      country: 'spain',
      latitude: 0,
      longitude: 0,
      enclosures: [],
    },
  };

  private servmatConfig: Servmat = {
    server: {
      mapName: 'servmat name',
      IP: '120.0.0.1',
      port: 5123,
      logDir: 'C:\\masterPX\\logs',
      logLevel: 0,
    },
  };

  private comServerConfig: ComServer = {
    com_server: {
      mapName: 'COMServer name',
      IP: '127.0.0.1',
      port: 4001,
      logDir: 'C:\\masterPX\\logs',
      logLevel: 0,
      devices: {
        ioDevices: [],
        readers: [],
        printers: [],
        fuel_dispenser_lines: [],
        weighers: [],
      },
    },
  };

  private ocrServersConfig: OCRServersContainer = {
    ocr_servers: [],
  };

  private authserverConfig: AuthServer = {
    auth_server: {
      mapName: 'Authorization server name',
      IP: '127.0.0.1',
      port: 5454,
      logDir: 'C:\\masterPX\\logs',
      openAlways: false,
      loadServices: false,
      loadPRLProviderEmployees: false,
      mysql: {
        host: '127.0.0.1',
        database: 'access_manager',
      },
    },
  };

  private lanesConfig: LanesContainer = {
    lanes: [],
  };

  private webclientConfig: WebClient = {
    serverURL: 'http://localhost:5000',
    wsURI: 'ws://localhost:5000',
    liveViewUpdateTime: 5000,
  };

  // tslint:disable-next-line:max-line-length
  constructor(
    public configService: ConfigService,
    public localConfig: AppConfigService,
    public formBuilder: FormBuilder,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private snackBar: MatSnackBar
  ) {
    this.cfg = null;
  }

  ngOnDestroy(): void {
    this.configSubscription.unsubscribe();
    this.configBackupSubscription.unsubscribe();
  }

  ngOnInit() {
    this.breadcrumbData = {
      titles: {
        icon: 'settings_input_component',
        links: [
          {
            text: 'Configuración instalación',
          },
        ],
      },
      description: 'Aquí puede ver o consultar la configuración de la instalación',
      buttons: [
        {
          text: 'Cargar configuración',
          action: () => this.loadFile(),
          icon: 'upload_file',
          disabled: false
        },
        {
          text: 'Guardar cambios',
          action: () => this.saveChanges(),
          icon: 'save',
          disabled: false
        },
      ],
    };

    this.wccfg = this.localConfig.getConfig();

    this.configSubscription = this.configService.getConfig().subscribe(
      (data: any) => {
        const allConfigParsed: JSON = JSON.parse(JSON.stringify(data));
        this.cfg = isEmpty(allConfigParsed) ? {} : allConfigParsed;
        // tslint:disable-next-line:curly
        if (isEmpty(this.cfg)) this.configNotFound();
        // tslint:disable-next-line:curly
        if (!isEmpty(this.cfg)) this.initPage();
      },
      () => {
        this.configNotFound();
      }
    );

    this.configBackupSubscription = this.configService.currentConfig$.subscribe((backupConfig) => {
      this.cfg = backupConfig;
      this.initPage();
      this.isBackup = true;
      this.snackBar.open(
        'Esta visualizando una configuración cargada, si desea que la aplicación utilice esta configuración seleccione la opción Utilizar Configuración',
        '',
        { horizontalPosition: 'center', verticalPosition: 'top' }
      );
    });
  }

  isNumberField(value): boolean {
    return this.numericFields.includes(value);
  }

  private configNotFound() {
    this.openDialog('No se ha encontrado ninguna configuración', 'Desea crear una nueva?')
      .afterClosed()
      .subscribe((value) => {
        // tslint:disable-next-line:curly
        if (value === true) this.initPage();
        // tslint:disable-next-line:curly
        if (value === false) this.openSnackBar(`Configuración no encontrada`, '[ERROR]');
      });
  }

  private initPage() {
    this.initForms();
    this.initValues();
  }

  private initForms() {
    this.generalForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      info: this.formBuilder.group({
        code: ['', [Validators.required]],
        country: ['', [Validators.required]],
        latitude: [null, [Validators.required]],
        longitude: [null, [Validators.required]],
        enclosures: this.formBuilder.array([]),
      }),
    });

    this.servmatForm = this.formBuilder.group({
      server: this.formBuilder.group({
        mapName: ['', [Validators.required]],
        IP: [null, [Validators.required, IPValidator]],
        port: [, [Validators.required, numberValidator, Validators.minLength(4)]],
        logDir: ['', [Validators.required]],
        logLevel: 1,
      }),
    });

    this.comserverForm = this.formBuilder.group({
      com_server: this.formBuilder.group({
        mapName: '',
        IP: ['', [Validators.required, IPValidator]],
        port: [null, [numberValidator, Validators.minLength(4)]],
        logDir: '',
        logLevel: '',
        devices: this.formBuilder.group({
          ioDevices: this.formBuilder.array([]),
          readers: this.formBuilder.array([]),
          printers: this.formBuilder.array([]),
          fuel_dispenser_lines: this.formBuilder.array([]),
          weighers: this.formBuilder.array([]),
        }),
      }),
    });

    this.ocrserversForm = this.formBuilder.group({
      ocr_servers: this.formBuilder.array([]),
    });

    this.authserverForm = this.formBuilder.group({
      auth_server: this.formBuilder.group({
        mapName: ['', [Validators.required]],
        IP: ['', [Validators.required, IPValidator]],
        port: [null, [Validators.required, numberValidator, Validators.minLength(4)]],
        logDir: ['', [Validators.required]],
        openAlways: '',
        loadServices: '',
        loadPRLProviderEmployees: '',
        mysql: this.formBuilder.group({
          host: ['', [Validators.required, IPValidator]],
          database: ['', [Validators.required]],
        }),
      }),
    });

    this.lanesForm = this.formBuilder.group({
      lanes: this.formBuilder.array([]),
    });

    this.webclientForm = this.formBuilder.group({
      serverURL: ['', Validators.required],
      wsURI: ['', Validators.required],
      liveView: '',
      liveViewUpdateTime: '',
    });
  }

  private initValues() {
    this.initGeneralFormValues();
    this.initServMatFormValues();
    this.initComServerFormValues();
    this.initOCRServerFormValues();
    this.initAuthServerFormValues();
    this.initLanesFormValues();
    this.initWebClientFormValues();

    this.mergeFormJSONS();
  }

  private mergeFormJSONS() {
    this.formsJSON = merge(this.formsJSON, this.generalForm.value);
    this.formsJSON = merge(this.formsJSON, this.servmatForm.value);
    this.formsJSON = merge(this.formsJSON, this.comserverForm.value);
    this.formsJSON = merge(this.formsJSON, this.ocrserversForm.value);
    this.formsJSON = merge(this.formsJSON, this.authserverForm.value);
    this.formsJSON = merge(this.formsJSON, this.lanesForm.value);
  }

  private initGeneralFormValues() {
    if (this.cfg != null) {
      if (this.cfg.name != null) {
        this.generalConfig.name = this.cfg.name;
        this.generalConfigJSON.name = this.cfg.name;
      }
      if (this.cfg.info != null) {
        this.generalConfig.info = this.cfg.info;
        this.generalConfigJSON.info = this.cfg.info;
      }
    }

    this.generalForm.patchValue(this.generalConfig);

    this.generalConfig.info.enclosures.forEach((enc) => {
      //avoid extra parameters to find differences
      let enclosure: Enclosure = {
        id: enc.id,
        name: enc.name,
      };
      ((((this.generalForm.get('info') as unknown) as FormGroup).get('enclosures') as unknown) as FormArray).push(this.formBuilder.group(enclosure));
    });
  }

  private initServMatFormValues() {
    if (this.cfg != null) {
      if (this.cfg.server != null) {
        this.servmatConfig.server = this.cfg.server;
        this.servmatConfigJSON.server = this.cfg.server;
      }
    }
    this.servmatForm.patchValue(this.servmatConfig);
  }

  private initComServerFormValues() {
    if (this.cfg != null) {
      if (this.cfg.com_server != null) {
        this.comServerConfig.com_server = this.cfg.com_server;
        this.comServerConfigJSON.com_server = this.cfg.com_server;
      }
    }
    this.comserverForm.patchValue(this.comServerConfig);
    if (this.comServerConfig.com_server.devices != null) {
      if (this.comServerConfig.com_server.devices.ioDevices != null) this.loadDeviceOnForm('ioDevices');
      if (this.comServerConfig.com_server.devices.readers != null) this.loadDeviceOnForm('readers');
      if (this.comServerConfig.com_server.devices.printers != null) this.loadDeviceOnForm('printers');
      if (this.comServerConfig.com_server.devices.fuel_dispenser_lines != null) this.loadDeviceOnForm('fuel_dispenser_lines');
      if (this.comServerConfig.com_server.devices.weighers != null) this.loadDeviceOnForm('weighers');
    }
  }

  private initOCRServerFormValues() {
    if (this.cfg != null) {
      if (this.cfg.ocr_servers != null) {
        this.ocrServersConfig.ocr_servers = this.cfg.ocr_servers;
        this.ocrServersConfigJSON.ocr_servers = this.cfg.ocr_servers;
      }
      this.ocrServersConfig.ocr_servers.forEach((server, index) => {
        (this.ocrserversForm.get('ocr_servers') as FormArray).push(this.newOCRServer);
        (this.ocrserversForm.get('ocr_servers') as FormArray).controls[index].patchValue(server);

        if (server.engines != null) {
          server.engines.forEach((eng: Engine) => {
            //avoid extra parameters to find differences
            let engine: Engine = {
              id: eng.id,
              mapName: eng.mapName,
              name: eng.name,
              type: eng.type,
              changeParamsPerCamera: eng.changeParamsPerCamera,
              coresLicensed: eng.coresLicensed,
              searchAllPlatesInFrame: eng.searchAllPlatesInFrame,
            };
            (((((this.ocrserversForm.get('ocr_servers') as unknown) as FormArray).controls[index] as unknown) as FormGroup).get(
              'engines'
            ) as FormArray).push(this.formBuilder.group(engine));
          });
        }
        if (server.cameras != null) {
          server.cameras.forEach((cam: Camera) => {
            let camera: Camera = {
              //avoid extra parameters to find differences
              id: cam.id,
              engineId: cam.engineId,
              mapName: cam.mapName,
              maxPictures: cam.maxPictures,
              method: cam.method,
              minConfidence: cam.minConfidence,
              msBetweenImagesEvent: cam.msBetweenImagesEvent,
              size: cam.size,
              slant: cam.slant,
              slope: cam.slope,
              stopReadingIfConf: cam.stopReadingIfConf,
              url: cam.url,
            };
            (((((this.ocrserversForm.get('ocr_servers') as unknown) as FormArray).controls[index] as unknown) as FormGroup).get(
              'cameras'
            ) as FormArray).push(this.formBuilder.group(camera));
          });
        }
      });
    }
  }

  private initAuthServerFormValues() {
    if (this.cfg != null) {
      if (this.cfg.auth_server != null) {
        this.authserverConfig.auth_server = this.cfg.auth_server;
        this.authServerConfigJSON.auth_server = this.cfg.auth_server;
      }
    }
    this.authserverForm.patchValue(this.authserverConfig);
  }

  private initLanesFormValues() {
    if (this.cfg != null) {
      if (this.cfg.lanes != null) {
        this.lanesConfig.lanes = this.cfg.lanes;
        this.lanesConfigJSON.lanes = this.cfg.lanes;
      }
    }

    this.lanesConfig.lanes.forEach((lane, index) => {
      (this.lanesForm.get('lanes') as FormArray).push(this.newLane);
      (this.lanesForm.get('lanes') as FormArray).controls[index].patchValue(lane);
      lane.identification?.plates?.front?.forEach((plate) =>
        ((((((this.lanesForm.get('lanes') as unknown) as FormArray).controls[index].get('identification') as FormGroup).get(
          'plates'
        ) as FormGroup).get('front') as unknown) as FormArray).push(this.formBuilder.group(plate))
      );
    });
  }

  private initWebClientFormValues() {
    if (this.cfg != null) {
      if (this.wccfg != null) {
        this.webclientConfig = this.wccfg;
        this.webClientConfigJSON = this.wccfg;
      }
    }
    this.webclientForm.patchValue(this.webclientConfig);
  }

  private loadDeviceOnForm(deviceName: string) {
    this.comServerConfig.com_server.devices[deviceName].forEach((dev: Device) => {
      //avoid extra parameters to find differences
      let device: Device = {
        id: dev.id,
        mapName: dev.mapName,
        IP: dev.IP,
        port: dev.port,
        driver: dev.driver,
      };

      ((((((this.comserverForm.get('com_server') as unknown) as FormGroup).get('devices') as unknown) as FormGroup).get(
        deviceName
      ) as unknown) as FormArray).push(this.formBuilder.group(device));
    });
  }

  private loadFile() {
    this.bottomSheet.open(ImportSheetComponent);
  }

  private isValid(): boolean {
    if (!this.generalForm.valid && this.generalForm.touched) {
      this.showValidationErrors('General');
      return false;
    }
    if (!this.servmatForm.valid && this.servmatForm.touched) {
      this.showValidationErrors('Servmat');
      return false;
    }
    if (!this.authserverForm.valid && this.authserverForm.touched) {
      this.showValidationErrors('Com Server');
      return false;
    }
    if (!this.comserverForm.valid && this.comserverForm.touched) {
      this.showValidationErrors('OCR Server');
      return false;
    }
    if (!this.ocrserversForm.valid && this.ocrserversForm.touched) {
      this.showValidationErrors('Auth Server');
      return false;
    }
    if (!this.lanesForm.valid && this.lanesForm.touched) {
      this.showValidationErrors('Carriles');
      return false;
    }
    return true;
  }

  private hasChanges() {
    if (this.generalForm.touched) return true;
    if (this.servmatForm.touched) return true;
    if (this.authserverForm.touched) return true;
    if (this.comserverForm.touched) return true;
    if (this.ocrserversForm.touched) return true;
    if (this.lanesForm.touched) return true;
    return false;
  }

  private showValidationErrors(tab: string) {
    this.openSnackBar(`Hay errores de validación en el formulario de la pestaña ${tab}`, '[ERROR]');
  }

  private isWebClientTab(): boolean {
    if (this.formTabs.selectedIndex === WEB_CLIENT_TAB_IDX) {
      this.openSnackBar('La configuración del Web Client debe guardarse utilizando los botones de guardado del formulario', '[INFO]');
      return true;
    }
    return false;
  }

  private isDirty(): boolean {
    let hasChanges = this.hasChanges();
    if (!hasChanges) {
      this.openSnackBar('No se han realizado cambios en la configuración', '[INFO]');
      return false;
    }
    return true;
  }

  saveChanges(jsonData?: any): boolean {
    if (this.isWebClientTab()) return false;
    if (!this.isDirty()) return false;
    if (!this.isValid()) return false;

    this.openSaveDialog('Guardar Archivo', 'Solo se guardarán los cambios de los formularios modificados ¿Desea guardar el archivo de configuración?')
      .afterClosed()
      .subscribe((result: SaveDialogResult) => {
        if (result.save) result.backup ? this.saveFile(jsonData, result.fileName) : this.saveFile(jsonData);
      });
  }

  private saveFile(jsonData?: any, filename?: string) {
    const configToSave = isEmpty(jsonData) ? {} : { ...jsonData };

    if (isEmpty(configToSave.name)) configToSave.name = this.generalForm.touched ? this.generalForm.value.name : this.cfg.name;
    this.generalConfig.name = this.generalForm.value.name;
    if (isEmpty(configToSave.info)) configToSave.info = this.generalForm.touched ? this.generalForm.value.info : this.cfg.info;
    this.generalConfig.info = this.generalForm.value.info;
    if (isEmpty(configToSave.server)) configToSave.server = this.servmatForm.touched ? this.servmatForm.value.server : this.cfg.server;
    this.servmatConfig.server = this.servmatForm.value.server;
    if (isEmpty(configToSave.auth_server))
      configToSave.auth_server = this.authserverForm.touched ? this.authserverForm.value.auth_server : this.cfg.auth_server;
    this.authserverConfig.auth_server = this.authserverForm.value.auth_server;
    if (isEmpty(configToSave.com_server))
      configToSave.com_server = this.comserverForm.touched ? this.comserverForm.value.com_server : this.cfg.com_server;
    this.comServerConfig.com_server = this.comserverForm.value.com_server;
    if (isEmpty(configToSave.ocrServersConfig))
      configToSave.ocr_servers = this.ocrserversForm.touched ? this.ocrserversForm.value.ocr_servers : this.cfg.ocr_servers;
    this.ocrServersConfig = this.ocrserversForm.value;
    if (isEmpty(configToSave.lanesConfig)) configToSave.lanes = this.lanesForm.touched ? this.lanesForm.value.lanes : this.cfg.lanes;
    this.lanesConfig = this.lanesForm.value;

    this.generalForm.reset(this.generalForm);
    this.servmatForm.reset(this.servmatForm);
    this.authserverForm.reset(this.authserverForm);
    this.comserverForm.reset(this.comserverForm);
    this.ocrserversForm.reset(this.ocrserversForm);
    this.lanesForm.reset(this.lanesForm);

    this.generalForm.patchValue(this.generalConfig);
    this.servmatForm.patchValue(this.servmatConfig);
    this.authserverForm.patchValue(this.authserverConfig);
    this.comserverForm.patchValue(this.comServerConfig);
    this.comserverForm.patchValue(this.comServerConfig);
    this.ocrserversForm.patchValue(this.ocrServersConfig);
    this.lanesForm.patchValue(this.lanesConfig);

    if (isEmpty(filename)) {
      this.configService.saveConfig(configToSave).subscribe(
        (currentConfig) => {
          this.configService.setCurrentConfig(currentConfig, false);
          this.openSnackBar('Configuración guardada correctamente', '[OK]');
        },
        () => {
          this.openSnackBar('Error guardando la Configuración', '[ERROR]');
        }
      );
    } else {
      this.configService.saveConfigWithBackup(configToSave, filename).subscribe(
        (currentConfig) => {
          this.configService.setCurrentConfig(currentConfig, false);
          this.openSnackBar('Configuración guardada correctamente', '[OK]');
        },
        () => {
          this.openSnackBar('Error guardando la Configuración', '[ERROR]');
        }
      );
    }
  }

  saveLocalChanges(jsonData?: any) {
    if (this.webclientForm.valid) {
      let configToSave = isEmpty(jsonData) ? {} : { ...jsonData };

      configToSave = this.webclientForm.value;
      this.webclientForm.reset(this.webclientForm);
      this.webclientForm.patchValue(this.webclientConfig);

      this.localConfig.saveConfig(configToSave).subscribe(
        () => {
          this.openSnackBar('Configuración de Web Client guardada correctamente', '[OK]');
        },
        () => {
          this.openSnackBar('Error guardando la configuración de Web Client', '[ERROR]');
        }
      );
    }

    if (!this.webclientForm.valid) {
      this.showValidationErrors('Web Client');
    }
  }

  // ***************************  DIALOG   *************************

  private openDialog(title: string, message: string) {
    return this.dialog.open(DeleteDialogComponent, {
      data: { msg1: title, msg2: message },
    });
  }

  private openSaveDialog(title: string, message: string) {
    return this.dialog.open(SaveDialogComponent, {
      data: { title: title, message: message },
    });
  }

  private openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, { duration: 3000 });
  }

  private showDeleteDialog(elementName: string, id: any, resultCallback: (value: any) => any) {
    const dialogRef = this.openDialog('Va a borrar el siguiente elemento', `${elementName} - ${id}`);
    dialogRef.afterClosed().subscribe((value) => {
      resultCallback(value);
    });
  }

  private setFormDirty(form: FormGroup) {
    form.markAsDirty();
    form.markAllAsTouched();
  }

  // *************************** OCR SERVERS ***********************
  get newOCRServer(): FormGroup {
    let ocrId: number = 0;
    ocrId = this.listOCRServers().length;

    return this.formBuilder.group({
      id: [ocrId, [Validators.required, numberValidator]],
      mapName: 'OCRServer name',
      IP: ['127.0.0.1', [Validators.required, IPValidator]],
      port: [5001, [Validators.required, numberValidator, Validators.minLength(4)]],
      eventsDir: ['C:/Events', [Validators.required]],
      imagesDir: ['C:/Images', [Validators.required]],
      logDir: ['C:/masterPX/logs', [Validators.required]],
      logLevel: 0,
      engines: this.formBuilder.array([]),
      cameras: this.formBuilder.array([]),
    });
  }

  listOCRServers() {
    let servers = [];
    servers = this.ocrserversForm.get('ocr_servers').value;
    return servers;
  }

  addOCRServer() {
    (this.ocrserversForm.get('ocr_servers') as FormArray).push(this.newOCRServer);
    this.setFormDirty(this.ocrserversForm);
    this.ocrServersTabGroup.selectedIndex = this.listOCRServers().length - 1;
  }

  deleteOCRServer(serverId: any) {
    this.showDeleteDialog('OCR Server', serverId, (value) => {
      if (value) {
        (this.ocrserversForm.get('ocr_servers') as FormArray).removeAt(serverId);
        this.setFormDirty(this.ocrserversForm);
      }
    });
  }

  // **************************  ENCLOSURES  ************************
  get newEnclosure(): FormGroup {
    let newid: number = 0;
    let newName: string;
    if (this.generalForm != null && ((this.generalForm.controls['info'] as FormGroup).controls['enclosures'] as FormArray).length !== 0) {
      newid = ((this.generalForm.controls['info'] as FormGroup).controls['enclosures'] as FormArray).length;
      newName = 'Nuevo Recinto';
    }
    return this.formBuilder.group({
      id: [newid, [Validators.required, numberValidator]],
      name: [newName, [Validators.required]],
    });
  }

  listEnclosures() {
    let enclosures = [];
    enclosures = ((this.generalForm.get('info') as FormGroup).get('enclosures') as FormArray).value;
    return enclosures;
  }

  addEnclosure(): void {
    ((this.generalForm.controls['info'] as FormGroup).controls['enclosures'] as FormArray).push(this.newEnclosure);
    this.setFormDirty(this.generalForm);
  }

  deleteEnclosure(id: number): void {
    this.showDeleteDialog('Recinto', id, (value) => {
      if (value) {
        ((this.generalForm.controls['info'] as FormGroup).controls['enclosures'] as FormArray).removeAt(id);
        this.setFormDirty(this.generalForm);
      }
    });
  }

  // **************************  DEVICES  ***************************
  newDevice(deviceType: string) {
    let newId: number = 0;
    newId = this.listDevices(deviceType).length;

    let port: number = 4001;

    if (deviceType === 'printers') port = 9100;
    if (deviceType === 'readers') port = 4001;

    return this.formBuilder.group({
      id: [newId, [Validators.required]],
      mapName: [deviceType, [Validators.required]],
      IP: ['127.0.0.1', [Validators.required, IPValidator]],
      port: [port, [numberValidator]],
      driver: '',
    });
  }

  listDevices(deviceType: string) {
    let ioDevices = [];
    ioDevices = (((this.comserverForm.get('com_server') as FormGroup).get('devices') as FormGroup).get(deviceType) as FormArray).value;
    return ioDevices;
  }

  getIoDeviceTypeById(iodeviceId: any): string {
    let type = '';
    let ioDevices = [];
    ioDevices = (((this.comserverForm.get('com_server') as FormGroup).get('devices') as FormGroup).get('ioDevices') as FormArray).value;
    ioDevices.forEach((element) => {
      if (element.id == iodeviceId) {
        if (element.driver === 'ioLogik') type = 'ioLogik';
        else type = 'Elka';
      }
    });
    return type;
  }

  private addDevice(deviceName: string): void {
    (((((this.comserverForm.get('com_server') as unknown) as FormGroup).get('devices') as FormGroup).get(deviceName) as unknown) as FormArray).push(
      this.newDevice(deviceName)
    );
    this.setFormDirty(this.comserverForm);
  }

  private deleteDevice(deviceName: string, id: any): void {
    this.showDeleteDialog(deviceName, id, (value) => {
      if (value) {
        (((((this.comserverForm.get('com_server') as unknown) as FormGroup).get('devices') as FormGroup).get(
          deviceName
        ) as unknown) as FormArray).removeAt(id);
        this.setFormDirty(this.comserverForm);
      }
    });
  }

  addIoDevice(): void {
    this.addDevice('ioDevices');
  }
  deleteIoDevice(id: any): void {
    this.deleteDevice('ioDevices', id);
  }

  addReader(): void {
    this.addDevice('readers');
  }
  deleteReader(id: any): void {
    this.deleteDevice('readers', id);
  }

  addPrinter(): void {
    this.addDevice('printers');
  }
  deletePrinter(id: any): void {
    this.deleteDevice('printers', id);
  }

  addFuelDispenser(): void {
    this.addDevice('fuel_dispenser_lines');
  }
  deleteFuelDispenser(id: any): void {
    this.deleteDevice('fuel_dispenser_lines', id);
  }

  addWeigher(): void {
    this.addDevice('weighers');
  }
  deleteWeigher(id: any): void {
    this.deleteDevice('weighers', id);
  }

  // **************************  ENGINES  ***************************
  get newEngine() {
    return this.formBuilder.group({
      id: 1,
      mapName: '',
      type: '',
      name: '',
      coresLicensed: 2,
      searchAllPlatesInFrame: false,
      changeParamsPerCamera: false,
    });
  }

  listEngines(serverId: any) {
    const servers = this.ocrserversForm.get('ocr_servers').value;
    let engines = [];
    servers.forEach((server: any) => {
      if (server.id === serverId) engines = server.engines;
    });
    return engines;
  }

  addEngine(idServer: any): void {
    ((((this.ocrserversForm.get('ocr_servers') as unknown) as FormArray).controls[idServer] as FormGroup).get('engines') as FormArray).push(
      this.newEngine
    );
    this.setFormDirty(this.ocrserversForm);
  }

  deleteEngine(idServer: any, idEngine: any): void {
    this.showDeleteDialog('Motor', idEngine, (value) => {
      if (value) {
        ((((this.ocrserversForm.get('ocr_servers') as unknown) as FormArray).controls[idServer] as FormGroup).get('engines') as FormArray).removeAt(
          idEngine
        );
        this.setFormDirty(this.ocrserversForm);
      }
    });
  }

  // ***************************  CAMERAS  *********************************

  get newCamera() {
    return this.formBuilder.group({
      id: -1,
      mapName: '',
      url: '',
      engineId: 0,
      maxPictures: 5,
      minConfidence: 65,
      stopReadingIfConf: 75,
      msBetweenImagesEvent: 500,
      method: '',
      size: 10,
      slant: -10,
      slope: 0,
    });
  }

  listCameras(serverId: any) {
    const servers = this.ocrserversForm.get('ocr_servers').value;
    let cameras = [];
    servers.forEach((server: any) => {
      if (parseInt(server.id) === parseInt(serverId)) cameras = server.cameras;
    });
    return cameras;
  }

  addCamera(idServer: any): void {
    ((((this.ocrserversForm.get('ocr_servers') as unknown) as FormArray).controls[idServer] as FormGroup).get('cameras') as FormArray).push(
      this.newCamera
    );
    this.setFormDirty(this.ocrserversForm);
  }

  deleteCamera(idServer: any, idCamera: any): void {
    this.showDeleteDialog('Camara', idCamera, (value) => {
      if (value) {
        ((((this.ocrserversForm.get('ocr_servers') as unknown) as FormArray).controls[idServer] as FormGroup).get('cameras') as FormArray).removeAt(
          idCamera
        );
        this.setFormDirty(this.ocrserversForm);
      }
    });
  }

  // **********************************  LANES   ********************************

  get newLane(): FormGroup {
    let laneId: number = 0;
    laneId = this.listLanes().length;

    return this.formBuilder.group({
      id: [laneId, [Validators.required, numberValidator]],
      mapName: ['Nuevo Carril', [Validators.required]],
      general: this.formBuilder.group({
        laneType: ['vehicles', [Validators.required]],
        direction: ['in', [Validators.required]],
        encFrom: [0, [Validators.required, numberValidator]],
        encTo: [1, [Validators.required, numberValidator]],
        statesMachine: ['statesMachine', [Validators.required]],
        bidireccional: false,
        reverseLane: -1,
        laneEnabled: true,
        autoDetecDirectionMode: false,
      }),
      identification: this.formBuilder.group({
        presence: this.formBuilder.group({
          deviceId: [0, [Validators.required, numberValidator]],
          io: '',
          inv: false,
        }),
        plates: this.formBuilder.group({
          front: this.formBuilder.array([]),
        }),
      }),
      gate: this.formBuilder.group({
        sensors: this.formBuilder.group({
          security: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
          bar_open: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
          closed: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
          blocked: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
          photocell: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
        }),
        actions: this.formBuilder.group({
          open: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
          close: this.formBuilder.group({
            deviceId: [0, [Validators.required, numberValidator]],
            io: '',
            inv: false,
          }),
        }),
      }),
    });
  }

  listLanes() {
    let lanes = [];
    lanes = this.lanesForm.get('lanes').value;
    return lanes;
  }

  addLane(): void {
    (this.lanesForm.get('lanes') as FormArray).push(this.newLane);
    this.setFormDirty(this.lanesForm);
    this.lanesTabGroup.selectedIndex = this.listLanes().length - 1;
  }

  deleteLane(laneId: any): void {
    this.showDeleteDialog('Carril', laneId, (value) => {
      if (value) {
        (this.lanesForm.get('lanes') as FormArray).removeAt(laneId);
        this.setFormDirty(this.lanesForm);
      }
    });
  }

  // *******************************  FrontPlates  ****************************

  get newFrontPlate() {
    return this.formBuilder.group({
      ocrServerId: [, [Validators.required, numberValidator]],
      camId: [, [Validators.required, numberValidator]],
      trigger: 'presence',
    });
  }

  addFrontPlate(laneId: any): void {
    ((((this.lanesForm.get('lanes') as FormArray).controls[laneId].get('identification') as FormGroup).get('plates') as FormGroup).get(
      'front'
    ) as FormArray).push(this.newFrontPlate);
    this.setFormDirty(this.lanesForm);
  }

  deleteFrontPlate(laneId: any, plateId: any): void {
    this.showDeleteDialog('Matrícula frontal', plateId, (value) => {
      if (value) {
        ((((this.lanesForm.get('lanes') as FormArray).controls[laneId].get('identification') as FormGroup).get('plates') as FormGroup).get(
          'front'
        ) as FormArray).removeAt(plateId);
        this.setFormDirty(this.lanesForm);
      }
    });
  }
}
